import { TokenAmount, Pair, Currency } from '@uniswap/sdk'
import { useEffect, useMemo, useState } from 'react'
import IUniswapV2PairABI from '@uniswap/v2-core/build/IUniswapV2Pair.json'
import { Interface } from '@ethersproject/abi'
import { useActiveWeb3React } from '../hooks'

import { useMultipleContractSingleData } from '../state/multicall/hooks'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { Contract, ethers } from 'ethers'
import NewAbi from '../abis/NewAbi.json'
import NewRouterAbi from '../abis/NewRouterAbi.json'
import { useLocation } from 'react-router-dom'
import { ROUTER_ADDRESS } from '../constants'

const PAIR_INTERFACE = new Interface(IUniswapV2PairABI.abi)
const PAIR_INTERFACE2 = new Interface(NewRouterAbi)
const GET_PAIR_INTERFACE = new Interface(NewAbi.abi)

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
  SKIPPED,
}

export function usePairs(currencies: [Currency | undefined, Currency | undefined][]): [PairState, Pair | null][] {
  const { chainId } = useActiveWeb3React()

  const POL_NATIVE_ADDRESS = '0x0000000000000000000000000000000000001010'
  const WETH_ADDRESS = {
    decimals: 18,
    symbol: 'POL',
    name: 'POL Token',
    chainId: 137,
    address: '0x95A2206c01DE8Ba7730DD3CBa16d6961b9736AaF',
    tokenInfo: {
      name: 'POL Token',
      address: '0x95A2206c01DE8Ba7730DD3CBa16d6961b9736AaF',
      symbol: 'POL',
      decimals: 18,
      chainId: 137,
      logoURI: 'http://localhost:3000/tokens-image/pol-token.png',
    },
    tags: [],
  }

  const location: any = useLocation()

  if (location.pathname === '/swap') {
    const tokens = useMemo(
      () =>
        currencies.map(([currencyA, currencyB]) => [
          wrappedCurrency(currencyA, chainId),
          wrappedCurrency(currencyB, chainId),
        ]),
      [chainId, currencies]
    )

    const provider = useMemo(
      () =>
        new ethers.providers.JsonRpcProvider(
          'https://polygon-mainnet.g.alchemy.com/v2/THjT9IccqPam8aUXz90I52OMXk3-j9qz' //JXuLTkzJefXEvz0lGKv7TiInN9cOyDlf
        ),
      []
    )

    const factoryContract = useMemo(
      () => new ethers.Contract('0x74b1c2b3B6E349aCC674Fb350BA34Bdb549c27F2', GET_PAIR_INTERFACE, provider), //0x5bEf6dafD2120bEE075dFD0D971E28502810CDD0 0xcE15835777ccc944D532604bEF3232Db382FfE18
      [provider]
    )

    const [pairAddresses, setPairAddresses] = useState<string[]>([])
    const [pairAddressCache, setPairAddressCache] = useState<Map<string, string>>(new Map())

    const [pools, setPools] = useState<any>([])
    const [poolAddresses, setPoolAddresses] = useState<string[]>([])
    const [statuses, setStatuses] = useState<boolean[]>([])

    const UpdateStatus = async () => {
      try {
        const readStatus = new Contract(ROUTER_ADDRESS, PAIR_INTERFACE2, provider)
        const poolData = await readStatus?.getPools()

        if (
          !poolData ||
          !Array.isArray(poolData) ||
          poolData.length < 10 ||
          !Array.isArray(poolData[1]) ||
          poolData[1].length === 0
        ) {
          console.warn('getPools() boş veya geçersiz veri döndürdü.')
          setPools([])
          setStatuses([])
          setPoolAddresses([])
          return
        }

        const [
          creators,
          pools,
          token0s,
          token1s,
          amount0s,
          amount1s,
          statuses,
          timestamps,
          firstInAmounts,
          firstOutAmounts,
        ] = poolData

        const formattedPools = pools.map((poolAddress: string, index: number) => ({
          creator: creators[index],
          poolAddress,
          token0: token0s[index],
          token1: token1s[index],
          amount0: amount0s[index].toString(),
          amount1: amount1s[index].toString(),
          status: statuses[index],
          timestamp: new Date(Number(timestamps[index]) * 1000).toISOString(),
          firstInAmount: firstInAmounts[index].toString(),
          firstOutAmount: firstOutAmounts[index].toString(),
        }))

        setPools(formattedPools)
        setStatuses(statuses)
        setPoolAddresses(pools)
      } catch (error) {
        console.error('getPools çağrısı başarısız oldu:', error)
        setPools([])
        setStatuses([])
        setPoolAddresses([])
      }
    }

    useEffect(() => {
      UpdateStatus()
    }, [])

    useEffect(() => {
      console.log('Pool Addresses:', poolAddresses)
      console.log('Statuses:', statuses)
    }, [poolAddresses, statuses])
    useEffect(() => {
      const getPairAddresses = async () => {
        const newPairAddresses: string[] = []
        const updatedCache = new Map(pairAddressCache)

        const [tokenA, tokenB] = tokens[0] || []

        if (tokenA && tokenB && !tokenA.equals(tokenB)) {
          const cacheKey = `${tokenA.address}-${tokenB.address}`
          if (pairAddressCache.has(cacheKey)) {
            newPairAddresses.push(pairAddressCache.get(cacheKey)!)
          } else {
            try {
              const address = await factoryContract.getPair(
                tokenA.address === POL_NATIVE_ADDRESS ? WETH_ADDRESS.address : tokenA.address,
                tokenB.address === POL_NATIVE_ADDRESS ? WETH_ADDRESS.address : tokenB.address
              )
              updatedCache.set(cacheKey, address)

              newPairAddresses.push(address)
            } catch (error) {
              console.error(`Error fetching pair for ${cacheKey}`, error)
            }
          }
        }

        if (newPairAddresses.length > 0) {
          setPairAddresses(newPairAddresses)
          setPairAddressCache(updatedCache)
        }
      }

      if (tokens.length > 0) {
        getPairAddresses()
      }
    }, [tokens])

    const results = useMultipleContractSingleData(pairAddresses, PAIR_INTERFACE, 'getReserves')

    console.log('pools', pools)

    return useMemo(() => {
      return results.map((result, i) => {
        const { result: reserves, loading } = result
        const tokenA = tokens[i] ? tokens[i][0] : undefined
        const tokenB = tokens[i] ? tokens[i][1] : undefined

        if (loading) return [PairState.LOADING, null]
        if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]

        if (!reserves || !Array.isArray(reserves) || reserves.length < 2) {
          console.warn('Invalid reserves for tokens:', { tokenA, tokenB, reserves })
          return [PairState.NOT_EXISTS, null]
        }

        const reserve0 = reserves[0]
        const reserve1 = reserves[1]

        const reserve0Safe = reserve0 ? reserve0.toString() : '0'
        const reserve1Safe = reserve1 ? reserve1.toString() : '0'

        const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]

        const poolAddress = pairAddresses[i]
        const status = pools?.find((pool: any) => pool.poolAddress === poolAddress)?.status
        console.log('status-check:', status, 'for pool:', poolAddress)

        if (status === false) {
          return [PairState.NOT_EXISTS, null]
        }

        return [
          PairState.EXISTS,
          new Pair(new TokenAmount(token0, reserve0Safe), new TokenAmount(token1, reserve1Safe)),
        ]
      })
    }, [results, tokens, pairAddresses, pools])
  } else {
    const tokens = useMemo(
      () =>
        currencies.map(([currencyA, currencyB]) => [
          wrappedCurrency(currencyA, chainId),
          wrappedCurrency(currencyB, chainId),
        ]),
      [chainId, currencies]
    )

    const provider = useMemo(
      () =>
        new ethers.providers.JsonRpcProvider(
          'https://polygon-mainnet.g.alchemy.com/v2/THjT9IccqPam8aUXz90I52OMXk3-j9qz'
        ),
      []
    )

    const factoryContract = useMemo(
      () => new ethers.Contract('0x74b1c2b3B6E349aCC674Fb350BA34Bdb549c27F2', GET_PAIR_INTERFACE, provider), //0x72776DE100DdEc6E9C08F307900343872420CBcB
      [provider]
    )

    const [pairAddresses, setPairAddresses] = useState<string[]>([])
    const [pairAddressCache, setPairAddressCache] = useState<Map<string, string>>(new Map())

    useEffect(() => {
      const getPairAddresses = async () => {
        const newPairAddresses: string[] = []
        const updatedCache = new Map(pairAddressCache)

        for (const [tokenA, tokenB] of tokens) {
          if (!tokenA || !tokenB || tokenA.equals(tokenB)) continue

          const cacheKey = `${tokenA.address}-${tokenB.address}`
          if (pairAddressCache.has(cacheKey)) {
            newPairAddresses.push(pairAddressCache.get(cacheKey)!)
          } else {
            try {
              const address = await factoryContract.getPair(
                tokenA.address === POL_NATIVE_ADDRESS ? WETH_ADDRESS.address : tokenA.address,
                tokenB.address === POL_NATIVE_ADDRESS ? WETH_ADDRESS.address : tokenB.address
              )
              updatedCache.set(cacheKey, address)

              newPairAddresses.push(address)
            } catch (error) {
              console.error(`Error fetching pair for ${cacheKey}`, error)
            }
          }
        }
        function arraysAreEqual(arr1: any[], arr2: any[]): boolean {
          if (arr1.length !== arr2.length) return false
          return arr1.every((value, index) => value === arr2[index])
        }

        if (newPairAddresses.length > 0 && !arraysAreEqual(pairAddresses, newPairAddresses)) {
          setPairAddresses(newPairAddresses)
          setPairAddressCache(updatedCache)
        }
      }

      if (tokens.length > 0) {
        getPairAddresses()
      }
    }, [tokens, factoryContract, pairAddressCache, pairAddresses])

    const results = useMultipleContractSingleData(pairAddresses, PAIR_INTERFACE, 'getReserves')
    console.log('pair-adres', pairAddresses)
    return useMemo(() => {
      return results.map((result, i) => {
        const { result: reserves, loading } = result
        const tokenA = tokens[i] ? tokens[i][0] : undefined
        const tokenB = tokens[i] ? tokens[i][1] : undefined

        if (loading) return [PairState.LOADING, null]
        if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]
        if (!reserves || !Array.isArray(reserves) || reserves.length < 2) {
          console.warn('Invalid reserves for tokens:', { tokenA, tokenB, reserves })
          return [PairState.NOT_EXISTS, null]
        }

        const reserve0 = reserves[0]
        const reserve1 = reserves[1]

        const reserve0Safe = reserve0 ? reserve0.toString() : '0'
        const reserve1Safe = reserve1 ? reserve1.toString() : '0'

        const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]

        return [
          PairState.EXISTS,
          new Pair(new TokenAmount(token0, reserve0Safe), new TokenAmount(token1, reserve1Safe)),
        ]
      })
    }, [results, tokens])
  }
}

export function usePair(tokenA?: Currency, tokenB?: Currency): [PairState, Pair | null] {
  const result = usePairs([[tokenA, tokenB]])[0]
  if (result) {
    return result
  } else {
    return [PairState.NOT_EXISTS, null]
  }
}
